/**
 * MenuItem Component
 * Individual menu item with keyboard shortcut display
 */

import { memo } from 'react'

export interface MenuItemConfig {
  label?: string
  shortcut?: string
  action?: () => void
  icon?: string
  checked?: boolean
  disabled?: boolean
  type?: 'normal' | 'separator' | 'submenu'
  submenu?: MenuItemConfig[]
}

interface MenuItemProps {
  item: MenuItemConfig
  onClose?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  isFocused?: boolean
}

function MenuItem({
  item,
  onClose,
  onMouseEnter,
  onMouseLeave,
  isFocused = false
}: MenuItemProps) {
  // Separator
  if (item.type === 'separator') {
    return <div className="menu-separator" role="separator" />
  }

  // Regular menu item
  const handleClick = () => {
    if (item.disabled || !item.action) return

    item.action()
    onClose?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <div
      role="menuitem"
      className={`menu-item ${item.disabled ? 'disabled' : ''} ${isFocused ? 'focused' : ''} ${item.checked ? 'checked' : ''}`}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={handleKeyDown}
      tabIndex={item.disabled ? -1 : 0}
      aria-disabled={item.disabled}
    >
      {/* Checkbox indicator for toggle items */}
      <span className="menu-item-check">
        {item.checked && '✓'}
      </span>

      {/* Icon */}
      {item.icon && (
        <span className="menu-item-icon">{item.icon}</span>
      )}

      {/* Label */}
      <span className="menu-item-label">{item.label}</span>

      {/* Keyboard Shortcut */}
      {item.shortcut && (
        <span className="menu-item-shortcut">{item.shortcut}</span>
      )}

      {/* Submenu indicator */}
      {item.type === 'submenu' && (
        <span className="menu-item-arrow">▸</span>
      )}
    </div>
  )
}

export default memo(MenuItem)
